import { useEffect, useState } from 'react';

import { Text } from '@/components/ui/catalyst/text';

import {
  RequestPageProvider,
  useRequestPage,
} from './contexts/RequestPageContext';
import { HipaaPackageTab } from './HipaaPackageTab';
import { RequestPageHeader } from './RequestPageHeader';
import { RequestPageSidebar } from './RequestPageSidebar';
import { ReviewSubmitPage } from './review-submit';
import { ActivityLogTab } from './tabs/activity-log/ActivityLogTab';
import { InvoiceList } from './tabs/invoices/InvoiceList';
import { RecordsList } from './tabs/records/RecordsList';
import { VoiceAICallTab } from './tabs/voice-ai/VoiceAICallTab';
import type { RequestPageProps, TabType } from './types';

// ============================================================================
// Main Component
// ============================================================================

/**
 * Main request page component with consolidated context provider
 * Manages the full-screen modal for handling treatment record requests
 */
export const RequestPage = ({
  requestId,
  providerId,
  onClose,
  onLayoutModeChange,
}: RequestPageProps) => {
  return (
    <RequestPageProvider
      requestId={requestId}
      providerId={providerId}
      onClose={onClose}
    >
      <RequestPageContent onLayoutModeChange={onLayoutModeChange} />
    </RequestPageProvider>
  );
};

// ============================================================================
// Content Component
// ============================================================================

/**
 * Inner component that consumes the unified context
 * Handles tab navigation and content rendering
 */
const RequestPageContent = ({
  onLayoutModeChange,
}: {
  onLayoutModeChange?: (mode: 'default' | 'review') => void;
}) => {
  const { request, provider, isLoadingRequest, isLoadingProvider, viewMode } =
    useRequestPage();

  const [activeTab, setActiveTab] = useState<TabType>('hipaa');

  useEffect(() => {
    onLayoutModeChange?.(viewMode);
  }, [onLayoutModeChange, viewMode]);

  // Loading state
  if (isLoadingProvider || isLoadingRequest) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <div className="text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600 mx-auto mb-4" />
          <Text className="text-zinc-500 font-medium">
            Loading request details...
          </Text>
        </div>
      </div>
    );
  }

  // Error state
  if (!provider || !request) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <div className="text-center">
          <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="h-8 w-8 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-label="Error icon"
            >
              <title>Error</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <Text className="text-zinc-700 font-medium mb-2">
            Request Not Found
          </Text>
          <Text className="text-zinc-500 text-sm">
            Unable to load request data
          </Text>
        </div>
      </div>
    );
  }

  if (viewMode === 'review') {
    return <ReviewSubmitPage />;
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header with breadcrumb and tabs */}
      <RequestPageHeader activeTab={activeTab} onTabChange={setActiveTab} />

      {/* 3-Column Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar: Request Configuration */}
        <RequestPageSidebar />

        {/* Center + Right: Tab Content */}
        <div className="flex-1 overflow-hidden bg-zinc-50">
          <TabContent activeTab={activeTab} />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Tab Content Component
// ============================================================================

/**
 * Renders content based on active tab selection
 * Each tab is a full-height component that manages its own layout
 */
const TabContent = ({ activeTab }: { activeTab: TabType }) => {
  const { request, provider, requestId, providerId } = useRequestPage();

  // Safety check (should never happen due to parent check)
  if (!request || !provider) return null;

  switch (activeTab) {
    case 'hipaa':
      // HIPAA tab manages its own layout and overflow
      return <HipaaPackageTab />;

    case 'records':
      return (
        <div className="h-full overflow-y-auto p-6">
          <RecordsList request={request} />
        </div>
      );

    case 'invoices':
      return (
        <div className="h-full overflow-y-auto p-6">
          <InvoiceList request={request} />
        </div>
      );

    case 'activity':
      return (
        <div className="h-full overflow-y-auto p-6">
          <ActivityLogTab request={request} provider={provider} />
        </div>
      );

    case 'voice-ai-call':
      return (
        <div className="h-full overflow-y-auto p-6">
          <VoiceAICallTab requestId={requestId} providerId={providerId} />
        </div>
      );

    default:
      return null;
  }
};
